// ABOUTME: Hook to read CPQ settings (hierarchy IDs) from the SettingsContext.
// ABOUTME: Provides typed access to CPQ configuration for UI components.

import { CpqSettings } from '@src/__generated__/types';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext, useMemo } from 'react';

/**
 * Reads CPQ settings from the SettingsContext.
 * Returns null if CPQ is not initialized.
 */
export function useCpqSettings(): CpqSettings | null {
    const settings = useContext(SettingsContext);

    return useMemo(() => {
        const cpq = settings?.cpq;
        if (!cpq?.enable) return null;

        return cpq;
    }, [settings]);
}

/**
 * Returns whether the CPQ cart feature is enabled.
 */
export function useIsCartEnabled(): boolean {
    const cpqSettings = useCpqSettings();
    return cpqSettings?.enableCart ?? false;
}

/**
 * Returns whether the configurator's "Find Similar" button is enabled.
 * Opt-in: the button is hidden unless the setting is explicitly true.
 */
export function useIsFindSimilarEnabled(): boolean {
    const cpqSettings = useCpqSettings();
    return cpqSettings?.enableFindSimilar ?? false;
}

/**
 * Returns whether the configurator's "Add linked product" button is enabled.
 * Opt-in: the button is hidden unless the setting is explicitly true.
 */
export function useIsAddLinkedProductEnabled(): boolean {
    const cpqSettings = useCpqSettings();
    return cpqSettings?.enableAddLinkedProduct ?? false;
}

/**
 * Returns whether the configurator's "Add temporary feature" button is enabled.
 * Opt-in: the button is hidden unless the setting is explicitly true.
 */
export function useIsAddTemporaryFeatureEnabled(): boolean {
    const cpqSettings = useCpqSettings();
    return cpqSettings?.enableAddTemporaryFeature ?? false;
}

/**
 * Returns whether the configurator's "Add temporary warranty" button is enabled.
 * Opt-in: the button is hidden unless the setting is explicitly true.
 */
export function useIsAddTemporaryWarrantyEnabled(): boolean {
    const cpqSettings = useCpqSettings();
    return cpqSettings?.enableAddTemporaryWarranty ?? false;
}

/**
 * Returns whether the configurator's additional-info affordances are enabled: the action-header
 * button and the per-option one, which the configurator drives from the same flag.
 * Opt-in: hidden unless the setting is explicitly true.
 */
export function useIsAdditionalInfoEnabled(): boolean {
    const cpqSettings = useCpqSettings();
    return cpqSettings?.enableAdditionalInfo ?? false;
}
